import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [cartDiscountSuccess] - The success element.
 * @property {HTMLElement} [cartDiscountSuccessText] - The success text element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #isUpdating = false;

  /**
   * Creates a controller for the current request.
   * We intentionally do not abort the previous request here. Aborting cart requests can leave the UI
   * stale when a newer request fails or when Shopify returns sections slower than expected.
   */
  #createAbortController() {
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Updates the rendered cart discount section from the Shopify bundled section response.
   *
   * @param {Record<string, string | null | undefined>} sections - The sections object returned by Shopify.
   * @returns {boolean}
   */
  #renderCurrentSection(sections) {
    if (typeof this.dataset.sectionId !== 'string') return false;

    const html = sections?.[this.dataset.sectionId];
    if (typeof html !== 'string' || !html.trim()) return false;

    // Pass the full section HTML back to the theme renderer. The Section Rendering API returns
    // complete section markup, and the theme renderer is responsible for targeting the live section.
    morphSection(this.dataset.sectionId, html);
    return true;
  }

  /**
   * Handles failures where the cart mutation succeeded but the bundled section HTML is missing/stale.
   * A hard reload is preferable to silently leaving the cart UI out of sync with Shopify's cart state.
   */
  #recoverStaleCartUi() {
    window.location.reload();
  }

  /**
   * Handles updates to the cart discount.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isUpdating) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    this.#isUpdating = true;
    const abortController = this.#createAbortController();

    try {
      const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Cart discount update failed with status ${response.status}`);
      }

      const data = await response.json();

      if (
        data.discount_codes?.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || !newHtml.trim()) {
        this.#recoverStaleCartUi();
        return;
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__remove') || [];
      const codes = Array.from(discountCodes)
        .map((element) => (element instanceof HTMLElement ? element.dataset.discountCode : null))
        .filter(Boolean);

      // Before morphing, check whether a valid shipping discount was accepted by the cart API but
      // not rendered in this UI. In that case we show the existing shipping-specific message.
      const appliedDiscount = data.discount_codes?.find((discount) => {
        return discount.code === discountCodeValue && discount.applicable === true;
      });

      if (
        codes.length === existingDiscounts.length &&
        codes.every((code) => existingDiscounts.includes(code)) &&
        appliedDiscount &&
        appliedDiscount.type === 'shipping'
      ) {
        this.#handleDiscountError('shipping');
        discountCode.value = '';
        return;
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      if (!this.#renderCurrentSection(data.sections)) {
        this.#recoverStaleCartUi();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#recoverStaleCartUi();
    } finally {
      this.#activeFetch = null;
      this.#isUpdating = false;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isUpdating) return;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      (!(event instanceof MouseEvent) && !(event instanceof KeyboardEvent)) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__remove');
    if (!(pill instanceof HTMLElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    this.#isUpdating = true;
    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Cart discount removal failed with status ${response.status}`);
      }

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      if (!this.#renderCurrentSection(data.sections)) {
        this.#recoverStaleCartUi();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#recoverStaleCartUi();
    } finally {
      this.#activeFetch = null;
      this.#isUpdating = false;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Handles the discount success.
   *
   * @param {string} code - The discount code.
   */
  #handleDiscountSuccess(code) {
    const { cartDiscountError, cartDiscountSuccess, cartDiscountSuccessText } = this.refs;

    cartDiscountError.classList.add('hidden');

    if (cartDiscountSuccess && cartDiscountSuccessText) {
      cartDiscountSuccessText.textContent = `Discount code ${code} applied successfully!`;
      cartDiscountSuccess.classList.remove('hidden');

      setTimeout(() => {
        cartDiscountSuccess.classList.add('hidden');
      }, 5000);
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__remove');

    for (const pill of discountPills) {
      if (pill instanceof HTMLElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
